/** Registro série a série do atleta (A4): já vem preenchido com o prescrito; só o que for tocado é enviado. */
import { useEffect, useState } from "react";
import { api, ApiError } from "../api/client";
import type { PlannedWorkout, StrengthSetResult } from "../api/types";
import { describeSet } from "../lib/strengthEdit";
import { initLog, markAllDone, toPayload, touchedCount, type LogItem, type LogSet } from "../lib/strengthLog";
import { PARAM_LABEL } from "../lib/strength";
import { Button, Loading, useToast } from "./ui";
import { t, tn } from "../i18n";

const ERR: Record<string, () => string> = {
  reps: () => t("Repetições inválidas (0 a 1000)."),
  load: () => t("Carga inválida (0 a 1000 kg)."),
  duration: () => t("Duração inválida. Use mm:ss."),
  distance: () => t("Distância inválida (metros)."),
  rest: () => t("Descanso inválido (segundos, até 3600)."),
  rpe: () => t("Esforço inválido (1 a 10)."),
};

export function ProgressLine({ w }: { w: Pick<PlannedWorkout, "strength_progress"> }) {
  const p = w.strength_progress;
  if (!p) return null;
  if (p.state === "none") return <span className="muted">{t("Nenhuma série registrada ainda.")}</span>;
  const rec = p.sets_total - p.sets_pending;
  return (
    <span className={p.state === "complete" ? "sl-progress ok" : "sl-progress"}>
      {tn("{n} de {total} série registrada", "{n} de {total} séries registradas", rec, { total: p.sets_total })}
      {p.state === "partial" && ` · ${t("parcial")}`}
    </span>
  );
}

export function StrengthLog({ orgId, w, editable, onChanged }: { orgId: string; w: PlannedWorkout; editable: boolean; onChanged: (w: PlannedWorkout) => void }) {
  const toast = useToast();
  const strength = w.content.strength!;
  const [items, setItems] = useState<LogItem[] | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<ApiError | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .get<StrengthSetResult[]>(`/orgs/${orgId}/me/workouts/${w.id}/strength-results`)
      .then((saved) => alive && setItems(initLog(strength, saved)))
      .catch((e) => alive && setLoadError(e as ApiError));
    return () => {
      alive = false;
    };
  }, [orgId, w.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loadError) return <p className="error" role="alert">{loadError.message}</p>;
  if (!items) return <Loading />;

  const patch = (key: string, no: number, p: Partial<LogSet>) =>
    setItems(items.map((i) => (i.key !== key ? i : { ...i, sets: i.sets.map((s) => (s.log.setNo === no ? { ...s, log: { ...s.log, touched: true, ...p } } : s)) })));

  async function save() {
    if (!items) return;
    const { results, errors: errs } = toPayload(items);
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast(t("Há séries com valores inválidos."), "error");
      return;
    }
    if (!results.length) {
      toast(t("Registre ao menos uma série."), "error");
      return;
    }
    setBusy(true);
    try {
      const saved = await api.put<StrengthSetResult[]>(`/orgs/${orgId}/me/workouts/${w.id}/strength-results`, { results });
      setItems(initLog(strength, saved));
      onChanged(await api.get<PlannedWorkout>(`/orgs/${orgId}/me/workouts/${w.id}`));
      toast(t("Séries salvas."));
    } catch (err) {
      toast((err as ApiError).message, "error");
    } finally {
      setBusy(false);
    }
  }

  const labels = { kg: t("kg"), rest: t("de descanso"), reps: t("rep.") };
  return (
    <div className="strength-log">
      <div className="sl-head">
        <ProgressLine w={w} />
        {editable && (
          <Button size="sm" onClick={() => setItems(markAllDone(items))}>
            {t("Fiz tudo como prescrito")}
          </Button>
        )}
      </div>
      {items.map((item) => (
        <div key={item.key} className="sl-item">
          <div className="sv-item-head">
            <strong>{item.name}</strong>
            <span className="muted">{PARAM_LABEL[item.paramType]}</span>
          </div>
          {item.note && <p className="sv-note">{item.note}</p>}
          {item.sets.map(({ prescribed, log }) => {
            const id = `${log.itemKey}#${log.setNo}`;
            const f = (name: keyof LogSet, label: string, extra: Record<string, string> = {}) => (
              <div className="field">
                <label htmlFor={`${id}-${name}`}>{label}</label>
                <input id={`${id}-${name}`} className="input" value={String(log[name])} disabled={!editable || log.status === "skipped"}
                  onChange={(e) => patch(log.itemKey, log.setNo, { [name]: e.target.value } as Partial<LogSet>)}
                  aria-invalid={errors[id] ? true : undefined} {...extra} />
              </div>
            );
            return (
              <div key={id} className={`sl-set${log.touched ? " is-touched" : ""}`} role="group" aria-label={t("Série {n} de {name}", { n: log.setNo, name: item.name })}>
                <div className="sl-set-head">
                  <span className="sv-setno">{t("Série {n}", { n: log.setNo })}</span>
                  <span className="muted num">{t("Prescrito: {text}", { text: describeSet(prescribed, labels) })}</span>
                </div>
                <div className="sl-fields">
                  <div className="field">
                    <label htmlFor={`${id}-status`}>{t("Situação")}</label>
                    <select id={`${id}-status`} className="select" value={log.touched ? log.status : ""} disabled={!editable}
                      onChange={(e) => e.target.value && patch(log.itemKey, log.setNo, { status: e.target.value as LogSet["status"] })}>
                      {!log.touched && <option value="">{t("Não registrada")}</option>}
                      <option value="done">{t("Feita")}</option>
                      <option value="partial">{t("Parcial")}</option>
                      <option value="skipped">{t("Pulada")}</option>
                    </select>
                  </div>
                  {(item.paramType === "reps_load" || item.paramType === "reps") && f("reps", t("Repetições"), { inputMode: "numeric" })}
                  {item.paramType === "reps_load" && f("load", t("Carga (kg)"), { inputMode: "decimal" })}
                  {item.paramType === "time" && f("duration", t("Duração"), { placeholder: "mm:ss" })}
                  {item.paramType === "distance" && f("distance", t("Distância (m)"), { inputMode: "numeric" })}
                  {f("rest", t("Descanso (s)"), { inputMode: "numeric" })}
                  {f("rpe", t("Esforço (1 a 10)"), { inputMode: "numeric" })}
                </div>
                {errors[id] && <p className="field-error" role="alert">{ERR[errors[id]]?.()}</p>}
              </div>
            );
          })}
        </div>
      ))}
      {editable && (
        <div>
          <Button variant="primary" onClick={save} busy={busy} disabled={touchedCount(items) === 0}>
            {t("Salvar séries")}
          </Button>
        </div>
      )}
    </div>
  );
}

/** Leitura (treinador): séries realizadas pelo atleta. */
export function StrengthResultsReadOnly({ results }: { results: StrengthSetResult[] }) {
  if (!results.length) return <p className="muted">{t("O atleta ainda não registrou séries.")}</p>;
  const STATUS = { done: t("Feita"), partial: t("Parcial"), skipped: t("Pulada") };
  const groups = new Map<string, StrengthSetResult[]>();
  for (const r of results) groups.set(r.item_key, [...(groups.get(r.item_key) ?? []), r]);
  return (
    <div className="strength-view">
      {[...groups.entries()].map(([k, rows]) => (
        <div key={k} className="sv-item">
          <strong>{rows[0].exercise_name}</strong>
          <ol className="sv-sets">
            {rows.map((r) => (
              <li key={r.set_no}>
                <span className="sv-setno">{t("Série {n}", { n: r.set_no })}</span>
                <span className="num">
                  {[STATUS[r.status], r.reps !== null && `${r.reps} ${t("rep.")}`, r.load_kg !== null && `${String(Number(r.load_kg)).replace(".", ",")} ${t("kg")}`,
                    r.duration_s !== null && `${Math.floor(r.duration_s / 60)}:${String(r.duration_s % 60).padStart(2, "0")}`,
                    r.distance_m !== null && `${r.distance_m} m`, r.rpe !== null && `${t("esforço")} ${r.rpe}`].filter(Boolean).join(" · ")}
                </span>
              </li>
            ))}
          </ol>
        </div>
      ))}
    </div>
  );
}
